export const FIAT_DECIMALS = 2;
const FIAT_SCALE = BigInt(10 ** FIAT_DECIMALS);
const INTEGER_PATTERN = /^-?\d+$/;
const DECIMAL_PATTERN = /^-?\d+\.\d+$/;
type FiatAmountInput = string | number | bigint;

function normalizeInput(value: string | number): string {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new Error("金额格式无效");
    }
    return value.toString();
  }

  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error("金额不能为空");
  }
  if (!/^\d+(\.\d+)?$/.test(trimmed)) {
    throw new Error("金额格式无效");
  }
  return trimmed;
}

export function fiatToRaw(value: string | number): string {
  const normalized = normalizeInput(value);
  const [integerPart, fractionalPart = ""] = normalized.split(".");

  if (fractionalPart.length > FIAT_DECIMALS) {
    throw new Error(`最多支持 ${FIAT_DECIMALS} 位小数`);
  }

  const paddedFraction = fractionalPart.padEnd(FIAT_DECIMALS, "0");
  const raw =
    BigInt(integerPart) * FIAT_SCALE + BigInt(paddedFraction || "0");
  return raw.toString();
}

export function rawToFiat(
  rawValue: FiatAmountInput,
  keepTrailingZeros = false
): string {
  const normalizedRaw = normalizeRawDisplayInput(rawValue, keepTrailingZeros);
  if (normalizedRaw !== null) {
    return normalizedRaw;
  }

  return formatFiatRaw(rawValue, keepTrailingZeros);
}

/**
 * Strictly formats an integer FiatToken raw amount as yuan.
 * Unlike rawToFiat, this never guesses that a decimal input is already display currency.
 */
export function formatFiatRaw(
  rawValue: FiatAmountInput,
  keepTrailingZeros = false
): string {
  const value = parseFiatRaw(rawValue);
  const negative = value < 0n;
  const absolute = negative ? -value : value;

  const integerPart = absolute / FIAT_SCALE;
  const fractionalPart = (absolute % FIAT_SCALE)
    .toString()
    .padStart(FIAT_DECIMALS, "0");

  const fraction = keepTrailingZeros
    ? fractionalPart
    : fractionalPart.replace(/0+$/, "");

  const formatted = fraction
    ? `${integerPart.toString()}.${fraction}`
    : integerPart.toString();

  return negative ? `-${formatted}` : formatted;
}

/** Normalize an API/database yuan value without applying the raw-to-yuan scale. */
export function formatFiatDisplay(
  displayValue: string | number,
  keepTrailingZeros = false
): string {
  const normalized = normalizeDisplayValue(displayValue);
  const negative = normalized.startsWith("-");
  const unsigned = negative ? normalized.slice(1) : normalized;
  const [integerPart, fractionalPart = ""] = unsigned.split(".");

  if (fractionalPart.length > FIAT_DECIMALS) {
    const excess = fractionalPart.slice(FIAT_DECIMALS);
    if (/[^0]/.test(excess)) {
      throw new Error(`金额最多支持 ${FIAT_DECIMALS} 位小数`);
    }
  }

  const normalizedFraction = fractionalPart.slice(0, FIAT_DECIMALS);
  const formatted = formatDecimalString(
    `${negative ? "-" : ""}${integerPart}.${
      keepTrailingZeros
        ? normalizedFraction.padEnd(FIAT_DECIMALS, "0")
        : normalizedFraction
    }`,
    keepTrailingZeros
  );
  return formatted;
}

/** Compare two integer FiatToken raw amounts without converting them to Number. */
export function compareFiatRaw(left: FiatAmountInput, right: FiatAmountInput): number {
  const leftValue = parseFiatRaw(left);
  const rightValue = parseFiatRaw(right);
  return leftValue === rightValue ? 0 : leftValue < rightValue ? -1 : 1;
}

/** Multiply an integer FiatToken raw amount by an integer quantity exactly. */
export function multiplyFiatRaw(rawValue: FiatAmountInput, quantity: FiatAmountInput): string {
  return (parseFiatRaw(rawValue) * parseFiatRaw(quantity)).toString();
}

function parseFiatRaw(rawValue: FiatAmountInput): bigint {
  if (typeof rawValue === "bigint") {
    return rawValue;
  }
  if (typeof rawValue === "number") {
    if (!Number.isSafeInteger(rawValue)) {
      throw new Error("链上金额必须是安全整数或整数字符串");
    }
    return BigInt(rawValue);
  }

  const normalized = rawValue.trim();
  if (!INTEGER_PATTERN.test(normalized)) {
    throw new Error("链上金额必须是整数字符串");
  }
  return BigInt(normalized);
}

function normalizeDisplayValue(displayValue: string | number): string {
  if (typeof displayValue === "number") {
    if (!Number.isFinite(displayValue)) {
      throw new Error("金额格式无效");
    }
    if (Number.isInteger(displayValue) && !Number.isSafeInteger(displayValue)) {
      throw new Error("金额整数超出安全范围，请使用字符串");
    }
    return displayValue.toString();
  }

  const normalized = displayValue.trim();
  if (!INTEGER_PATTERN.test(normalized) && !DECIMAL_PATTERN.test(normalized)) {
    throw new Error("金额格式无效");
  }
  return normalized;
}

function normalizeRawDisplayInput(
  rawValue: string | number | bigint,
  keepTrailingZeros: boolean
): string | null {
  if (typeof rawValue === "bigint") {
    return null;
  }

  if (typeof rawValue === "number") {
    if (!Number.isFinite(rawValue) || Number.isInteger(rawValue)) {
      return null;
    }
    return formatDecimalString(rawValue.toString(), keepTrailingZeros);
  }

  const trimmed = String(rawValue || "0").trim();
  if (!trimmed || INTEGER_PATTERN.test(trimmed)) {
    return null;
  }
  if (!DECIMAL_PATTERN.test(trimmed)) {
    return "0";
  }
  return formatDecimalString(trimmed, keepTrailingZeros);
}

function formatDecimalString(value: string, keepTrailingZeros: boolean): string {
  const negative = value.startsWith("-");
  const normalized = negative ? value.slice(1) : value;
  const [integerPart, fractionalPart = ""] = normalized.split(".");
  const safeInteger = integerPart.replace(/^0+(?=\d)/, "") || "0";

  if (!fractionalPart) {
    return negative ? `-${safeInteger}` : safeInteger;
  }

  const safeFraction = keepTrailingZeros
    ? fractionalPart
    : fractionalPart.replace(/0+$/, "");
  const formatted = safeFraction ? `${safeInteger}.${safeFraction}` : safeInteger;

  return negative ? `-${formatted}` : formatted;
}
